const isBlockingModal = (dialog) => dialog.isConnected && dialog.matches(':modal');

// A <dialog> opened with showModal() renders in the browser's top layer, which no z-index on a
// regular element can reach, and it marks every node outside its own subtree inert. A flash in
// <body> was therefore both buried and dead to clicks.
//
// Showing this layer as a popover joins it to the top layer, and parenting it inside the topmost
// blocking dialog keeps its close button clickable and its message in the accessibility tree. A
// popover is positioned against the viewport wherever it sits, so it also escapes the transforms
// and overflow clipping of the dialogs it moves into.
//
// "manual" rather than "auto": an auto popover takes the Escape key that has to reach the dialog,
// and each new one light-dismisses the last.
export default function flashLayer() {
  // showModal() sets the open attribute, so the order dialogs gain it is the order they stack in
  // the top layer. The DOM exposes no other way to tell which one is on top, and only that one
  // leaves its subtree interactive.
  let openModals = [];

  const trackModal = (element) => {
    if (element.tagName !== 'DIALOG') return;

    const index = openModals.indexOf(element);

    if (isBlockingModal(element)) {
      if (index === -1) openModals.push(element);
    } else if (index > -1) {
      openModals.splice(index, 1);
    }
  };

  const currentHost = () => {
    openModals = openModals.filter(isBlockingModal);

    return openModals[openModals.length - 1] ?? document.body;
  };

  return {
    init() {
      if (!this.$root.showPopover) return;

      openModals = Array.from(document.querySelectorAll('dialog')).filter(isBlockingModal);
      this.sync();

      new MutationObserver(() => this.sync()).observe(this.$root, { childList: true });

      new MutationObserver((records) => {
        records.forEach((record) => trackModal(record.target));
        this.sync();
      }).observe(document.documentElement, {
        subtree: true,
        attributes: true,
        attributeFilter: ['open'],
      });
    },

    sync() {
      this.rehome();

      const wanted = this.$root.children.length > 0;
      if (wanted === this.$root.matches(':popover-open')) return;

      if (wanted) this.$root.showPopover();
      else this.$root.hidePopover();
    },

    // The host is recomputed from the modal stack rather than from where the layer currently sits,
    // because a dialog that removes itself on close takes the layer along with it.
    rehome() {
      const host = currentHost();
      if (this.$root.parentElement === host) return;

      if (this.$root.matches(':popover-open')) this.$root.hidePopover();
      host.appendChild(this.$root);
    },
  };
}
